import { forwardRef } from 'react'

import { cn } from '@/lib/utils'

interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  size?: 'default' | 'small'
  disabled?: boolean
  className?: string
  // Accessible name. When the visible <label> wrapping the switch contains
  // dynamic count chips, screen readers concatenate the text and produce
  // jumbled names like "Show inactive1". Pass an explicit aria-label here
  // to set the accessible name independently of the visible decoration.
  'aria-label'?: string
}

const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onChange, size = 'default', disabled = false, className, ...rest }, ref) => {
    const isSmall = size === 'small'

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={rest['aria-label']}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1a3db5] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          isSmall ? 'h-4 w-8' : 'h-6 w-11',
          checked ? 'bg-[#1a3db5]' : 'bg-gray-200',
          className,
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block rounded-full bg-white shadow-md transition-transform duration-200',
            isSmall ? 'h-3 w-3' : 'h-5 w-5',
            isSmall
              ? checked ? 'translate-x-4' : 'translate-x-0.5'
              : checked ? 'translate-x-5' : 'translate-x-0.5',
            'mt-0.5',
          )}
        />
      </button>
    )
  },
)

Switch.displayName = 'Switch'

export { Switch }
export type { SwitchProps }
